class ProductGridOrdering extends HTMLElement {
  constructor() {
    super();
    // Do not run initialization here; use connectedCallback instead
  }

  connectedCallback() {
    // Only initialize once
    if (this._initialized) return;
    this._initialized = true;

    this.sectionId = this.dataset.section;
    this.productId = this.dataset.productId;
    this.is2dGrid = this.dataset.is2dGrid === 'true';
    // Feature flag: allow enabling/disabling injection into buy buttons
    // Treat only explicit truthy strings as enabled
    const injectAttr = String(this.dataset.injectEnabled || '').toLowerCase();
    this.injectEnabled = injectAttr === 'true' || injectAttr === '1' || injectAttr === 'yes';
    this.dropdownOptions = this.dataset.dropdownOptions || '';

    // Initialize event binding flag
    this._quantityEventsBound = false;

    const variantDataEl = this.querySelector('[data-grid-product-variants]');
    const optionsDataEl = this.querySelector('[data-grid-product-options]');

    if (!variantDataEl || !optionsDataEl) {
      console.error('Grid ordering: Required data elements not found');
      return;
    }

    try {
      const variantsJson = (variantDataEl.textContent || '').trim();
      const optionsJson = (optionsDataEl.textContent || '').trim();
      this.variants = JSON.parse(variantsJson);
      this.options = JSON.parse(optionsJson);
    } catch (e) {
      console.error('Grid ordering: Invalid data', e);
      return;
    }

    if (!this.options.yAxis) {
      console.error('Grid ordering: No yAxis option found');
      return;
    }

    // Central quantity storage - keyed by variant ID as per acceptance document
    this.selectedQuantities = {};

    // Modal elements
    this.modalTrigger = this.querySelector('.product-grid-ordering__modal-trigger');
    this.modalOverlay = this.querySelector('.product-grid-ordering__modal-overlay');
    this.modalClose = this.querySelector('.product-grid-ordering__modal-close');

    if (this.modalOverlay && this.modalOverlay.parentElement !== document.body) {
      document.body.appendChild(this.modalOverlay);
    }
    
    // Grid elements (within modal). Since the overlay is moved to <body>,
    // always query inside the overlay, not within the custom element.
    this.quantityInputs = this.modalOverlay.querySelectorAll('.product-grid-ordering__quantity-input');
    this.dropdowns = this.modalOverlay.querySelectorAll('.product-grid-ordering__dropdown');
    this.clearBtn = this.modalOverlay.querySelector('.product-grid-ordering__clear-btn');
    this.addToCartBtn = this.modalOverlay.querySelector('.product-grid-ordering__add-to-cart-btn');
    this.errorElement = this.modalOverlay.querySelector('.product-grid-ordering__error');
    this.errorMessage = this.modalOverlay.querySelector('.product-grid-ordering__error-message');
    this.totalQuantityEl = this.modalOverlay.querySelector('[data-total-quantity]');
    this.pageInfoEl = this.modalOverlay.querySelector('[data-page-info]');

    if (!this.modalTrigger || !this.modalOverlay) {
      console.error('Grid ordering: Required modal elements not found');
      return;
    }

    // Debug logging removed

    this.init();
  }
  
  init() {
    this.bindModalEvents();
    this.bindGridEvents();
    this.updateAddToCartButton();
    this.updatePageInfo();
    if (this.injectEnabled) {
      this.injectIntoBuyButtons();
    } else {
      // Ensure any previously injected proxy is removed (e.g., switching setting in Theme Editor)
      try {
        const sectionRoot = document.getElementById(`shopify-section-${this.sectionId}`) || document;
        sectionRoot
          .querySelectorAll('.product-form__buttons .product-grid-ordering__modal-trigger[data-proxy="true"]')
          .forEach((el) => el.remove());
        this.classList.remove('product-grid-ordering--injected');
      } catch (_) {}
    }
  }

  injectIntoBuyButtons() {
    if (this._injected) return;
    const tryInject = (reason = 'attempt') => {
      this._injectAttemptCount = (this._injectAttemptCount || 0) + 1;
      if (this._injected) return true;
      try {
        const sectionRoot = document.getElementById(`shopify-section-${this.sectionId}`) || document;
        const buyButtons = sectionRoot.querySelector('.product-form__buttons');
        if (!buyButtons) {
          return false;
        }

        // Prevent duplicate proxy if DOM was re-rendered
        if (buyButtons.querySelector('.product-grid-ordering__modal-trigger[data-proxy="true"]')) {
          this._injected = true;
          this.classList.add('product-grid-ordering--injected');
          return true;
        }

        // Create a proxy trigger button to live with native buy buttons
        const proxy = document.createElement('button');
        proxy.type = 'button';
        proxy.className = 'product-grid-ordering__modal-trigger button button--filled button--full uppercase';
        proxy.setAttribute('aria-label', 'Open bulk order grid');
        proxy.setAttribute('data-proxy', 'true');
        // Use original trigger label if available
        const label = this.modalTrigger?.textContent?.trim() || 'Bulk Order';
        proxy.textContent = label;

        // Insert after the primary submit button if present, otherwise append
        const primarySubmit = buyButtons.querySelector('.product-form__submit');
        if (primarySubmit && primarySubmit.parentElement === buyButtons) {
          primarySubmit.insertAdjacentElement('afterend', proxy);
        } else {
          buyButtons.appendChild(proxy);
        }

        // Wire up the proxy to open the modal
        proxy.addEventListener('click', () => this.openModal());

        // Mark as injected and hide the local trigger area via CSS hook
        this._injected = true;
        this.classList.add('product-grid-ordering--injected');
        this._proxyTrigger = proxy;
        return true;
      } catch (e) {
        // Keep warning to aid diagnosing failures
        console.warn('Grid ordering: Failed to inject into buy buttons', e);
        return false;
      }
    };

    // Attempt immediately
    if (tryInject('immediate')) return;

    // Retry shortly in case of parsing order
    setTimeout(() => tryInject('timeout-0'), 0);
    setTimeout(() => tryInject('timeout-150'), 150);
    setTimeout(() => tryInject('timeout-500'), 500);

    // Observe section for dynamic renders
    const sectionRoot = document.getElementById(`shopify-section-${this.sectionId}`) || document;
    if (sectionRoot && !this._buyButtonsObserver) {
      const observer = new MutationObserver((mutations) => {
        const missingProxy = !sectionRoot.querySelector('.product-form__buttons .product-grid-ordering__modal-trigger[data-proxy="true"]');
        if (missingProxy) {
          tryInject('mutation');
        }
      });
      observer.observe(sectionRoot, { childList: true, subtree: true });
      this._buyButtonsObserver = observer;
    }

    // In Theme Editor, watch for section (re)loads
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      const onSectionLoad = (event) => {
        if (event.target && event.target.id === `shopify-section-${this.sectionId}`) {
          tryInject('shopify:section:load');
        }
      };
      document.addEventListener('shopify:section:load', onSectionLoad);
    }
  }

  bindModalEvents() {
    // Modal trigger
    this.modalTrigger.addEventListener('click', () => {
      this.openModal();
    });

    // Modal close
    this.modalClose?.addEventListener('click', () => {
      this.closeModal();
    });

    // Close on overlay click
    this.modalOverlay.addEventListener('click', (e) => {
      if (e.target === this.modalOverlay) {
        this.closeModal();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modalOverlay.hidden) {
        this.closeModal();
      }
    });
  }

  openModal() {
    this.modalOverlay.hidden = false;
    document.body.style.overflow = 'hidden';
    
    // Dispatch event to trigger event binding on the pre-rendered grid
    this.dispatchEvent(new CustomEvent('gridRendered'));
    
    // Focus management
    this.modalClose?.focus();
    
    // Trap focus within modal
    this.trapFocus();
  }

  closeModal() {
    this.modalOverlay.hidden = true;
    document.body.style.overflow = '';
    this.modalTrigger.focus();
  }

  trapFocus() {
    const focusableElements = this.modalOverlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.modalOverlay.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    });
  }
  
  bindGridEvents() {
    // Wait for grid to be rendered before binding events
    this.addEventListener('gridRendered', () => {
      this.bindQuantityEvents();
      this.bindDropdownEvents();
      this.bindActionEvents();
    });
  }

  bindQuantityEvents() {
    // Use event delegation to prevent multiple event listeners when grid is re-rendered
    if (!this._quantityEventsBound) {
      this.modalOverlay.addEventListener('click', this.handleQuantityButtonClick.bind(this));
      this.modalOverlay.addEventListener('change', this.handleQuantityInputChange.bind(this));
      this.modalOverlay.addEventListener('input', this.handleQuantityInputInput.bind(this));
      this._quantityEventsBound = true;
    }
  }

  handleQuantityButtonClick(e) {
    const target = e.target.closest('.product-grid-ordering__quantity-btn');
    if (!target) return;

    const wrapper = target.closest('.product-grid-ordering__quantity-wrapper');
    if (!wrapper) return;

    const input = wrapper.querySelector('.product-grid-ordering__quantity-input');
    if (!input) return;

    if (target.classList.contains('product-grid-ordering__quantity-btn--minus')) {
      this.decreaseQuantity(input);
    } else if (target.classList.contains('product-grid-ordering__quantity-btn--plus')) {
      this.increaseQuantity(input);
    }
  }

  handleQuantityInputChange(e) {
    if (!e.target.classList.contains('product-grid-ordering__quantity-input')) return;
    this.handleQuantityChange(e.target);
  }

  handleQuantityInputInput(e) {
    if (!e.target.classList.contains('product-grid-ordering__quantity-input')) return;
    this.validateQuantityInput(e.target);
  }

  bindDropdownEvents() {
    // Dropdown change events for grid paging
    this.dropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', (e) => {
        this.handleDropdownChange();
      });
    });
  }

  bindActionEvents() {
    // Clear all button
    this.clearBtn?.addEventListener('click', () => {
      this.clearAllQuantities();
    });
    
    // Add to cart button
    this.addToCartBtn?.addEventListener('click', () => {
      this.addToCart();
    });
  }
  renderGrid() {
    if (!this.options.yAxis) return;

    const gridContainer = this.modalOverlay.querySelector('.product-grid-ordering__grid');
    if (!gridContainer) return;

    // Clear existing grid content
    gridContainer.innerHTML = '';

    // Get current dropdown values for grid paging
    const currentDropdownValues = {};
    this.dropdowns.forEach(dropdown => {
      currentDropdownValues[dropdown.dataset.optionPosition] = dropdown.value;
    });

    if (this.is2dGrid) {
      this.render2DGrid(gridContainer, currentDropdownValues);
    } else {
      this.render1DGrid(gridContainer, currentDropdownValues);
    }

    // Dispatch event that grid is rendered so we can bind events
    this.dispatchEvent(new CustomEvent('gridRendered'));
  }

  render2DGrid(container, dropdownValues) {
    const yAxis = this.options.yAxis;
    const xAxis = this.options.xAxis;

    // Create header
    const header = document.createElement('div');
    header.className = 'product-grid-ordering__header product-grid-ordering__header--2d';
    
    // Corner cell
    const cornerCell = document.createElement('div');
    cornerCell.className = 'product-grid-ordering__corner-cell';
    cornerCell.textContent = `${yAxis.name} \\ ${xAxis.name}`;
    header.appendChild(cornerCell);

    // X-axis header cells
    xAxis.values.forEach(xValue => {
      const cell = document.createElement('div');
      cell.className = 'product-grid-ordering__x-axis-label';
      cell.textContent = xValue;
      header.appendChild(cell);
    });

    container.appendChild(header);

    // Create data rows
    yAxis.values.forEach(yValue => {
      const row = document.createElement('div');
      row.className = 'product-grid-ordering__row product-grid-ordering__row--2d';
      row.dataset.yValue = yValue;

      // Y-axis label
      const yLabel = document.createElement('div');
      yLabel.className = 'product-grid-ordering__y-axis-label';
      yLabel.textContent = yValue;
      row.appendChild(yLabel);

      // Data cells
      xAxis.values.forEach(xValue => {
        const cell = this.create2DCell(yValue, xValue, dropdownValues);
        row.appendChild(cell);
      });

      container.appendChild(row);
    });
  }

  render1DGrid(container, dropdownValues) {
    const yAxis = this.options.yAxis;

    // Create header
    const header = document.createElement('div');
    header.className = 'product-grid-ordering__header product-grid-ordering__header--1d';
    
    const yLabel = document.createElement('div');
    yLabel.className = 'product-grid-ordering__y-axis-label';
    yLabel.textContent = yAxis.name;
    header.appendChild(yLabel);

    const quantityLabel = document.createElement('div');
    quantityLabel.className = 'product-grid-ordering__quantity-label';
    quantityLabel.textContent = 'Quantity';
    header.appendChild(quantityLabel);

    container.appendChild(header);

    // Create data rows
    yAxis.values.forEach(yValue => {
      const row = this.create1DRow(yValue, dropdownValues);
      container.appendChild(row);
    });
  }

  create2DCell(yValue, xValue, dropdownValues) {
    // Find variant for this combination
    const variant = this.findVariantForCell(yValue, xValue, dropdownValues);
    
    const cell = document.createElement('div');
    cell.className = 'product-grid-ordering__cell';
    cell.dataset.yValue = yValue;
    cell.dataset.xValue = xValue;

    if (!variant) {
      cell.classList.add('product-grid-ordering__cell--invalid');
      const invalidSpan = document.createElement('span');
      invalidSpan.className = 'product-grid-ordering__cell-invalid';
      invalidSpan.textContent = '—';
      cell.appendChild(invalidSpan);
    } else {
      cell.dataset.variantId = variant.id;
      cell.dataset.available = variant.available;

      if (!variant.available) {
        cell.classList.add('product-grid-ordering__cell--unavailable');
        const oosSpan = document.createElement('span');
        oosSpan.className = 'product-grid-ordering__cell-oos';
        oosSpan.title = 'Out of stock';
        oosSpan.textContent = 'OOS';
        cell.appendChild(oosSpan);
      } else {
        // Create quantity controls
        const quantityWrapper = this.createQuantityControls(variant, yValue, xValue);
        cell.appendChild(quantityWrapper);
      }
    }

    return cell;
  }

  create1DRow(yValue, dropdownValues) {
    // Find variant for this single option value
    const variant = this.findVariantForCell(yValue, null, dropdownValues);
    
    const row = document.createElement('div');
    row.className = 'product-grid-ordering__row product-grid-ordering__row--1d';
    row.dataset.yValue = yValue;

    if (!variant) {
      row.classList.add('product-grid-ordering__row--invalid');
    } else {
      row.dataset.variantId = variant.id;
      row.dataset.available = variant.available;
      if (!variant.available) {
        row.classList.add('product-grid-ordering__row--unavailable');
      }
    }

    // Y-axis value cell
    const yValueCell = document.createElement('div');
    yValueCell.className = 'product-grid-ordering__y-axis-value';
    yValueCell.textContent = yValue;

    if (!variant) {
      const invalidLabel = document.createElement('span');
      invalidLabel.className = 'product-grid-ordering__invalid-label';
      invalidLabel.textContent = 'N/A';
      yValueCell.appendChild(invalidLabel);
    } else if (!variant.available) {
      const unavailableLabel = document.createElement('span');
      unavailableLabel.className = 'product-grid-ordering__unavailable-label';
      unavailableLabel.textContent = 'Sold Out';
      yValueCell.appendChild(unavailableLabel);
    }

    row.appendChild(yValueCell);

    // Quantity cell
    const quantityCell = document.createElement('div');
    quantityCell.className = 'product-grid-ordering__quantity';

    if (variant && variant.available) {
      const quantityWrapper = this.createQuantityControls(variant, yValue);
      quantityCell.appendChild(quantityWrapper);
    } else {
      const unavailableSpan = document.createElement('span');
      unavailableSpan.className = 'product-grid-ordering__quantity-unavailable';
      unavailableSpan.textContent = '—';
      quantityCell.appendChild(unavailableSpan);
    }

    row.appendChild(quantityCell);
    return row;
  }

  createQuantityControls(variant, yValue, xValue = null) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-grid-ordering__quantity-wrapper';

    // Minus button
    const minusBtn = document.createElement('button');
    minusBtn.className = 'product-grid-ordering__quantity-btn product-grid-ordering__quantity-btn--minus';
    minusBtn.type = 'button';
    minusBtn.setAttribute('aria-label', `Decrease quantity for ${yValue}${xValue ? ` ${xValue}` : ''}`);
    // Create icon using theme's icon system
    const minusIcon = document.createElement('span');
    minusIcon.innerHTML = this.getIconSVG('theme-minus');
    minusBtn.appendChild(minusIcon);
    wrapper.appendChild(minusBtn);

    // Quantity input
    const input = document.createElement('input');
    input.className = 'product-grid-ordering__quantity-input';
    input.type = 'number';
    input.min = '0';
    input.max = '999';
    input.value = this.selectedQuantities[variant.id] || '0';
    input.dataset.variantId = variant.id;
    input.dataset.yValue = yValue;
    if (xValue) input.dataset.xValue = xValue;
    input.setAttribute('aria-label', `Quantity for ${yValue}${xValue ? ` ${xValue}` : ''}`);
    wrapper.appendChild(input);

    // Plus button
    const plusBtn = document.createElement('button');
    plusBtn.className = 'product-grid-ordering__quantity-btn product-grid-ordering__quantity-btn--plus';
    plusBtn.type = 'button';
    plusBtn.setAttribute('aria-label', `Increase quantity for ${yValue}${xValue ? ` ${xValue}` : ''}`);
    // Create icon using theme's icon system
    const plusIcon = document.createElement('span');
    plusIcon.innerHTML = this.getIconSVG('theme-plus');
    plusBtn.appendChild(plusIcon);
    wrapper.appendChild(plusBtn);

    return wrapper;
  }

  getIconSVG(iconName) {
    // Try to get existing icon from the page if available
    const existingIcon = document.querySelector(`svg[data-icon="${iconName}"] use, .icon--${iconName} use`);
    if (existingIcon) {
      return `<svg class="icon icon--${iconName}"><use href="${existingIcon.getAttribute('href')}"></use></svg>`;
    }
    
    // Fallback: create simple text-based icons
    const iconMap = {
      'theme-minus': '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 12h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>',
      'theme-plus': '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M12 6v12M6 12h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>',
      'theme-close': '<svg viewBox="0 0 24 24" width="20" height="20"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>'
    };
    
    return iconMap[iconName] || '';
  }
  
  handleQuantityChange(input) {
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value) || 0;
    
    // Update central quantity storage
    if (quantity > 0) {
      this.selectedQuantities[variantId] = quantity;
    } else {
      delete this.selectedQuantities[variantId];
    }
    
    this.validateQuantityInput(input);
    this.updateAddToCartButton();
    this.hideError();
  }
  
  handleDropdownChange() {
    // Re-render grid for current dropdown combination
    this.renderGrid();
    this.updatePageInfo();
  }

  findVariantForCell(yValue, xValue, dropdownValues) {
    return this.variants.find(variant => {
      // Check Y-axis match
      const yAxisOption = this.options.yAxis;
      const yMatch = this.getVariantOptionValue(variant, yAxisOption.position) === yValue;
      if (!yMatch) return false;
      
      // Check X-axis match (if 2D grid)
      if (this.is2dGrid && this.options.xAxis) {
        const xAxisOption = this.options.xAxis;
        const xMatch = this.getVariantOptionValue(variant, xAxisOption.position) === xValue;
        if (!xMatch) return false;
      }
      
      // Check dropdown matches
      if (this.options.dropdownOptions && this.options.dropdownOptions.length > 0) {
        for (const dropdownOption of this.options.dropdownOptions) {
          const expectedValue = dropdownValues[dropdownOption.position];
          const actualValue = this.getVariantOptionValue(variant, dropdownOption.position);
          if (actualValue !== expectedValue) return false;
        }
      }
      
      return true;
    });
  }
  
  getVariantOptionValue(variant, position) {
    switch (position) {
      case 1: return variant.option1;
      case 2: return variant.option2;
      case 3: return variant.option3;
      default: return null;
    }
  }
  
  getVariantsForDropdownValues(dropdownValues) {
    if (!this.options.dropdownOptions || this.options.dropdownOptions.length === 0) {
      return this.variants;
    }
    
    return this.variants.filter(variant => {
      return this.options.dropdownOptions.every(dropdownOption => {
        const expectedValue = dropdownValues[dropdownOption.position];
        const actualValue = this.getVariantOptionValue(variant, dropdownOption.position);
        return actualValue === expectedValue;
      });
    });
  }
  
  validateQuantityInput(input) {
    let value = parseInt(input.value) || 0;
    
    // Ensure value is within bounds
    if (value < 0) value = 0;
    if (value > 999) value = 999;
    
    input.value = value;
  }
  
  decreaseQuantity(input) {
    let value = parseInt(input.value) || 0;
    if (value > 0) {
      input.value = value - 1;
      this.handleQuantityChange(input);
    }
  }
  
  increaseQuantity(input) {
    let value = parseInt(input.value) || 0;
    if (value < 999) {
      input.value = value + 1;
      this.handleQuantityChange(input);
    }
  }
  
  clearAllQuantities() {
    // Clear central storage
    this.selectedQuantities = {};
    
    // Clear all visible inputs (query fresh from the overlay in case grid changed)
    this.modalOverlay.querySelectorAll('.product-grid-ordering__quantity-input').forEach(input => {
      input.value = 0;
    });
    
    this.updateAddToCartButton();
    this.hideError();
  }
  
  updateAddToCartButton() {
    const totalQuantity = Object.values(this.selectedQuantities).reduce((sum, qty) => sum + qty, 0);
    
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = totalQuantity === 0;
    }
    
    if (this.totalQuantityEl) {
      this.totalQuantityEl.textContent = totalQuantity;
    }
  }
  
  updatePageInfo() {
    if (!this.pageInfoEl || this.dropdowns.length === 0) return;
    
    const dropdownValues = Array.from(this.dropdowns).map(dropdown => dropdown.value);
    const pageDescription = dropdownValues.join(', ');
    this.pageInfoEl.textContent = `Page: ${pageDescription}`;
  }
  
  generateOptionCombinations(options) {
    if (options.length === 0) return [{}];
    if (options.length === 1) {
      const option = options[0];
      const optionValues = this.getOptionValues(option.position);
      return optionValues.map(value => ({ [option.position]: value }));
    }
    
    // For multiple options, generate all combinations
    const firstOption = options[0];
    const remainingOptions = options.slice(1);
    const firstOptionValues = this.getOptionValues(firstOption.position);
    const remainingCombinations = this.generateOptionCombinations(remainingOptions);
    
    const allCombinations = [];
    firstOptionValues.forEach(value => {
      remainingCombinations.forEach(remainingCombo => {
        allCombinations.push({
          [firstOption.position]: value,
          ...remainingCombo
        });
      });
    });
    
    return allCombinations;
  }
  
  getOptionValues(position) {
    // Find option values by position from the options data
    if (this.options.yAxis && this.options.yAxis.position === position) {
      return this.options.yAxis.values;
    }
    if (this.options.xAxis && this.options.xAxis.position === position) {
      return this.options.xAxis.values;
    }
    if (this.options.dropdownOptions) {
      const dropdownOption = this.options.dropdownOptions.find(opt => opt.position === position);
      if (dropdownOption) return dropdownOption.values;
    }
    return [];
  }
  

    async addToCart() {
    const items = this.getCartItems();
    
    if (items.length === 0) {
      this.showError('Please select at least one item to add to cart.');
      return;
    }
    
    this.setLoading(true);
    this.hideError();
    
    try {
      // Get cart drawer element for proper section updates
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      
      // Use fetchConfig like the theme's ProductForm for consistency
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      
      // Add items one by one to ensure proper cart drawer updates
      for (const item of items) {
        const formData = new FormData();
        formData.append('id', item.id);
        formData.append('quantity', item.quantity);
        
        if (cart) {
          // Include sections to render just like the standard form does
          formData.append(
            'sections',
            cart.getSectionsToRender().map(section => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        
        config.body = formData;
        
        const response = await fetch(routes.cart_add_url || window.routes.cart_add_url, config);
        const result = await response.json();
        
        // Handle errors like the theme's ProductForm
        if (result.status) {
          // Publish cart error event like the theme
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-grid-ordering",
              productVariantId: item.id,
              errors: result.errors || result.description,
              message: result.message
            });
          }
          throw new Error(result.description || Object.values(result.errors || {}).join(', '));
        }
        
        // Update cart drawer with the response (only for the last item to avoid multiple updates)
        if (cart && item === items[items.length - 1]) {
          cart.renderContents(result);
          // Remove is-empty class like the theme does
          if (cart.classList.contains("is-empty")) {
            cart.classList.remove("is-empty");
          }
        }
        
        // Publish cart update event like the theme (only for the last item)
        if (typeof publish === 'function' && item === items[items.length - 1]) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-grid-ordering",
            productVariantId: item.id,
            cartData: result
          });
        }
      }
      
      // Success - clear quantities and show feedback
      this.onAddToCartSuccess(items);
      
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showError(error.message || 'Unable to add items to cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
  
  getCartItems() {
    return Object.entries(this.selectedQuantities).map(([variantId, quantity]) => ({
      id: variantId,
      quantity: quantity
    }));
  }
  
  onAddToCartSuccess(items) {
    // Clear quantities after successful add
    this.clearAllQuantities();
    
    // Close the modal
    this.closeModal();
    
    // Show success message briefly
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const message = totalItems === 1 ? 
      `${totalItems} item added to cart` : 
      `${totalItems} items added to cart`;
    
    this.showSuccessMessage(message);
  }
  
  showSuccessMessage(message) {
    // Create temporary success element
    const successEl = document.createElement('div');
    successEl.className = 'product-grid-ordering__success';
    successEl.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgb(var(--color-filled-button));
      color: rgb(var(--color-filled-button-text));
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--card-corner-radius);
      z-index: 1000;
      font-weight: 500;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    successEl.textContent = message;
    
    document.body.appendChild(successEl);
    
    // Remove after 2 seconds
    setTimeout(() => {
      if (successEl.parentNode) {
        successEl.parentNode.removeChild(successEl);
      }
    }, 2000);
  }
  
  setLoading(loading) {
    this.classList.toggle('product-grid-ordering--loading', loading);
    
    // Handle loading state like the theme's ProductForm
    if (loading) {
      this.addToCartBtn?.setAttribute("aria-disabled", "true");
      this.addToCartBtn?.classList.add("loading");
      // Show loading spinner if it exists
      const spinner = this.modalOverlay.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.remove("hidden");
      }
    } else {
      this.addToCartBtn?.classList.remove("loading");
      // Hide loading spinner if it exists
      const spinner = this.modalOverlay.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.add("hidden");
      }
      // Only remove aria-disabled if we have items to add
      if (Object.keys(this.selectedQuantities).length > 0) {
        this.addToCartBtn?.removeAttribute("aria-disabled");
      }
    }
    
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = loading || Object.keys(this.selectedQuantities).length === 0;
    }
  }
  
  showError(message) {
    this.errorMessage.textContent = message;
    this.errorElement.hidden = false;
  }
  
  hideError() {
    this.errorElement.hidden = true;
  }
}

// Register the custom element
customElements.define('product-grid-ordering', ProductGridOrdering);

// Initialize grid ordering components when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product-grid-ordering').forEach(element => {
    if (!customElements.get('product-grid-ordering')) {
      // Fallback if custom elements not supported
      new ProductGridOrdering.prototype.constructor.call(element);
    }
  });
});

// Shopify theme events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const gridOrdering = event.target.querySelector('.product-grid-ordering');
    if (gridOrdering && !customElements.get('product-grid-ordering')) {
      new ProductGridOrdering.prototype.constructor.call(gridOrdering);
    }
  });
}
